import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Drawer,
  Button,
  Table,
  Tag,
  Space,
  Typography,
  Switch,
  Spin,
  Empty,
  Tooltip,
  notification,
  Badge,
} from 'antd';
import {
  DownloadOutlined,
  ReloadOutlined,
  ExportOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import { usePermissions } from '../../assets/utils/usePermissions';
import { queueFacilityExportJob, FORMAT_LABELS } from './facilityExportHelpers';

const { Text } = Typography;

const STATUS_CONFIG = {
  queued: { color: 'default', icon: <ClockCircleOutlined />, text: 'Queued' },
  processing: { color: 'processing', icon: <LoadingOutlined />, text: 'Processing' },
  completed: { color: 'success', icon: <CheckCircleOutlined />, text: 'Completed' },
  failed: { color: 'error', icon: <CloseCircleOutlined />, text: 'Failed' },
};

const FacilitiesExportsDrawer = ({ open, onClose, drawerZIndex }) => {
  const { hasAnyPermissionWithLegacy } = usePermissions();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [includeAll, setIncludeAll] = useState(false);
  const intervalRef = useRef(null);

  const isAdmin = hasAnyPermissionWithLegacy(['admin.*'], [1]);

  const fetchJobs = useCallback(async () => {
    try {
      const resp = await lib.listFacilityExportJobs(
        includeAll ? { include_all: 1 } : {}
      );
      const body = resp?.data;
      if (body?.error === 0 && Array.isArray(body?.data?.jobs || body?.data)) {
        const list = body.data.jobs || body.data;
        setJobs(list);
      }
    } catch (e) {
      console.error('Failed to fetch facility export jobs:', e?.message);
    }
  }, [includeAll]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchJobs().finally(() => setLoading(false));

    let pollCount = 0;
    const poll = () => {
      fetchJobs();
      pollCount += 1;
      const nextInterval = pollCount < 12 ? 5000 : 20000;
      intervalRef.current = setTimeout(poll, nextInterval);
    };
    intervalRef.current = setTimeout(poll, 5000);

    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
    };
  }, [open, fetchJobs]);

  const handleDownload = async (job) => {
    if (!job?.job_id) return;
    setDownloadingId(job.job_id);
    try {
      await lib.downloadFacilityExportJob(
        job.job_id,
        job.output_filename || 'facilities_export.csv'
      );
    } catch (e) {
      notification.error({
        message: 'Download failed',
        description: e?.message || 'Unable to download export file.',
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleRetry = async (job) => {
    if (!job?.params) return;
    try {
      await queueFacilityExportJob({
        ...job.params,
        format: job.params.format || 'csv',
      });
      notification.success({
        message: 'Export re-queued',
        description: 'A new facility export job has been submitted.',
      });
      fetchJobs();
    } catch (e) {
      notification.error({
        message: 'Retry failed',
        description: e?.message || 'Unable to retry export.',
      });
    }
  };

  const columns = [
    {
      title: 'Job ID',
      dataIndex: 'job_id',
      key: 'job_id',
      width: 180,
      ellipsis: true,
      render: (v) => (
        <Tooltip title={v}>
          <Text code style={{ fontSize: 12 }}>
            {v?.slice(0, 20)}…
          </Text>
        </Tooltip>
      ),
    },
    {
      title: 'Format',
      key: 'format',
      width: 80,
      render: (_, record) => {
        const fmt = record?.params?.format || 'csv';
        return FORMAT_LABELS[fmt] || String(fmt).toUpperCase();
      },
    },
    {
      title: 'Scope',
      key: 'scope',
      width: 100,
      render: (_, record) => record?.params?.scope || '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (v) => {
        const cfg = STATUS_CONFIG[v] || STATUS_CONFIG.queued;
        return (
          <Tag color={cfg.color} icon={cfg.icon}>
            {cfg.text}
          </Tag>
        );
      },
    },
    {
      title: 'Requested',
      dataIndex: 'requested_at',
      key: 'requested_at',
      width: 160,
      render: (v) => (v ? new Date(v).toLocaleString() : '—'),
    },
    {
      title: 'Rows',
      dataIndex: 'rows_exported',
      key: 'rows_exported',
      width: 80,
      render: (v) => (v != null ? v.toLocaleString() : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          {record.status === 'completed' && (
            <Tooltip title="Download">
              <Button
                type="primary"
                size="small"
                icon={<DownloadOutlined />}
                loading={downloadingId === record.job_id}
                onClick={() => handleDownload(record)}
              />
            </Tooltip>
          )}
          {record.status === 'failed' && (
            <Tooltip title="Retry">
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => handleRetry(record)}
              />
            </Tooltip>
          )}
          {record.status === 'queued' || record.status === 'processing' ? (
            <Spin size="small" />
          ) : null}
        </Space>
      ),
    },
  ];

  const hasActiveJobs = jobs.some(
    (j) => j.status === 'queued' || j.status === 'processing'
  );

  return (
    <Drawer
      title={
        <Space>
          <ExportOutlined />
          <span>Facility exports</span>
          {hasActiveJobs && <Badge status="processing" />}
        </Space>
      }
      placement="right"
      width={820}
      onClose={onClose}
      open={open}
      destroyOnClose
      zIndex={drawerZIndex}
    >
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Text type="secondary">
          {hasActiveJobs
            ? 'Some exports are still processing. The list refreshes automatically.'
            : `${jobs.length} export job(s) found.`}
        </Text>
        {isAdmin && (
          <Space>
            <Text type="secondary">All users</Text>
            <Switch
              checked={includeAll}
              onChange={(checked) => {
                setIncludeAll(checked);
                setLoading(true);
                fetchJobs().finally(() => setLoading(false));
              }}
            />
          </Space>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={jobs}
        rowKey="job_id"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        scroll={{ x: 720 }}
        locale={{
          emptyText: (
            <Empty description="No facility export jobs yet. Use Export from facility search to queue one." />
          ),
        }}
      />
    </Drawer>
  );
};

export default FacilitiesExportsDrawer;
